import React, { useState } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface InfoFieldItem {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value?: React.ReactNode;
  copyable?: boolean;
  copyText?: string;
  href?: string;
  isExternal?: boolean;
}

export interface InfoSection {
  title: string;
  fields: InfoFieldItem[];
}

interface EntityInfoSidebarProps {
  sections: InfoSection[];
  notes?: string | null;
  tags?: string[] | null;
  extraCard?: React.ReactNode;
}

export function EntityInfoSidebar({
  sections,
  notes,
  tags,
  extraCard,
}: EntityInfoSidebarProps) {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <div className="space-y-4">
      {sections.map((section, sIdx) => {
        const visibleFields = section.fields.filter(
          (f) => f.value !== null && f.value !== undefined && f.value !== ""
        );

        if (visibleFields.length === 0) return null;

        return (
          <div
            key={sIdx}
            className="bg-card border border-border/80 rounded-2xl p-5 shadow-2xs space-y-4"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground border-b pb-2.5">
              {section.title}
            </h3>

            <div className="space-y-3.5">
              {visibleFields.map((field, fIdx) => {
                const key = `${sIdx}-${fIdx}`;
                const Icon = field.icon;
                const textToCopy = field.copyText || (typeof field.value === "string" ? field.value : "");

                return (
                  <div key={fIdx} className="space-y-1 text-xs">
                    <div className="flex items-center gap-1.5 text-muted-foreground font-medium">
                      {Icon && <Icon className="size-3.5 shrink-0 text-muted-foreground/80" />}
                      <span>{field.label}</span>
                    </div>

                    <div className="flex items-center justify-between gap-2 font-medium text-foreground pl-5 break-words">
                      <div className="min-w-0">
                        {field.href ? (
                          <a
                            href={field.href}
                            target={field.isExternal ? "_blank" : undefined}
                            rel={field.isExternal ? "noopener noreferrer" : undefined}
                            className="text-primary hover:underline inline-flex items-center gap-1"
                          >
                            <span>{field.value}</span>
                            {field.isExternal && <ExternalLink className="size-3 shrink-0" />}
                          </a>
                        ) : (
                          <span>{field.value}</span>
                        )}
                      </div>

                      {field.copyable && textToCopy && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCopy(textToCopy, key)}
                          className="size-6 text-muted-foreground hover:text-foreground shrink-0 rounded-md"
                          title="Copy to clipboard"
                        >
                          {copiedKey === key ? (
                            <Check className="size-3 text-emerald-500" />
                          ) : (
                            <Copy className="size-3" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Tags Card */}
      {tags && tags.length > 0 && (
        <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-2xs space-y-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground border-b pb-2.5">
            Tags
          </h3>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {tags.map((t, idx) => (
              <span
                key={idx}
                className="text-[11px] font-medium px-2.5 py-1 rounded-lg bg-muted text-foreground border border-border/60"
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Notes Card */}
      {notes && (
        <div className="bg-card border border-border/80 rounded-2xl p-5 shadow-2xs space-y-2.5">
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground border-b pb-2.5">
            Notes & Remarks
          </h3>
          <p className="text-xs text-foreground/80 leading-relaxed whitespace-pre-wrap">
            {notes}
          </p>
        </div>
      )}

      {/* Extra Card slot */}
      {extraCard}
    </div>
  );
}
